import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAlerts } from "common";
import { API_URLS, API_STATUS } from "app/shared/constants";
import {
  marshalStudies,
  marshalStudyRecruitmentDataToFormik,
  marshalStudyRecruitmentMetrics,
} from "./serviceHelpers";

const StudyRecruitmentApi = () => {
  const { ALERT_TYPE, setAlert, clearAlert } = useAlerts();
  const { t } = useTranslation();

  const api = {
    /**
     * Gets all studies for a given uid
     */
    getStudies: async (uid) => {
      try {
        clearAlert();

        const studies = await axios.get(API_URLS.getStudies(uid));

        if (studies?.data) {
          const studiesData = marshalStudies(studies.data.values);
          return studiesData;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(t("messages.warning.api.studiesNotFound"));
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.response?.data?.message || e.message);
        console.error(
          t("messages.error.api.studiesNotFound"),
          e.response?.data?.message || e.message,
        );
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Gets the study recruitment data for a given protocol number
     */
    getStudyRecruitmentData: async (protocolNumber) => {
      try {
        clearAlert();

        const studyRecruitment = await axios.get(
          API_URLS.getStudyRecruitment(protocolNumber),
        );

        if (studyRecruitment?.data) {
          const studyRecruitmentData = marshalStudyRecruitmentDataToFormik(
            studyRecruitment.data,
          );
          return studyRecruitmentData;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(
            t("messages.warning.api.studyRecruitmentDataNotFound", {
              protocolNumber,
            }),
          );
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.response?.data?.message || e.message);
        console.error(
          t("messages.error.api.studyRecruitmentDataNotFound"),
          e.response?.data?.message || e.message,
        );
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Gets the study recruitment metrics for a given protocol number
     */
    getStudyRecruitmentMetrics: async (protocolNumber) => {
      try {
        clearAlert();

        const studyRecruitmentMetrics = await axios.get(
          API_URLS.getStudyRecruitmentMetrics(protocolNumber),
        );

        if (studyRecruitmentMetrics?.data) {
          const studyRecruitmentMetricsData = marshalStudyRecruitmentMetrics(
            studyRecruitmentMetrics.data,
          );
          return studyRecruitmentMetricsData;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(
            t("messages.warning.api.studyRecruitmentMetricsNotFound", {
              protocolNumber,
            }),
          );
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.response?.data?.message || e.message);
        console.error(
          t("messages.error.api.studyRecruitmentMetricsNotFound"),
          e.response?.data?.message || e.message,
        );
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Gets the Study Team Outreach Cohorts data for a given protocol number
     */
    getOutreachCohorts: async (protocolNumber) => {
      try {
        clearAlert();

        const outreachCohorts = await axios.get(
          API_URLS.getOutreachActivation(protocolNumber),
        );

        if (outreachCohorts?.data) {
          return outreachCohorts.data;
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(
            t("messages.warning.api.outreachCohortsNotFound", {
              protocolNumber,
            }),
          );
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.response?.data?.message || e.message);
        console.error(
          t("messages.error.api.outreachCohortsNotFound"),
          e.response?.data?.message || e.message,
        );
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Submits changes made to the study recruitment.
     *
     * @param {string} studyId The study ID
     * @param {object} formData Updated study recruitment
     * @returns Success or failure of the API call
     */
    saveStudyRecruitment: async (studyId, formData) => {
      try {
        clearAlert();

        const response = await axios.patch(
          API_URLS.saveStudyRecruitment(studyId),
          JSON.stringify(formData),
          {
            headers: {
              "Content-Type": "application/merge-patch+json",
            },
          },
        );

        if (response.status === 200) {
          return {
            status: API_STATUS.API_SUCCESS,
            data: marshalStudyRecruitmentDataToFormik(response.data),
          };
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(
            t("messages.warning.api.studyRecruitmentDataSaveFailed", {
              studyId,
            }),
          );
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.response?.data?.message || e.message);
        console.error(
          t("messages.error.api.studyRecruitmentDataSaveFailed"),
          e.response?.data?.message || e.message,
        );
        return API_STATUS.API_ERROR;
      }
    },

    /**
     * Submits changes made to the outreach cohort status
     *
     * @param {string} cohortId The outreach cohort ID
     * @param {object} payload The payload containing the new status and/or resume date for the outreach cohort
     * @returns Success or failure of the API call
     */
    updateOutreachCohortStatus: async (cohortId, payload) => {
      try {
        clearAlert();

        const response = await axios.patch(
          API_URLS.updateOutreachCohortStatus(cohortId),
          JSON.stringify(payload),
          {
            headers: {
              "Content-Type": "application/merge-patch+json",
            },
          },
        );

        if (response.status === 200) {
          return {
            status: API_STATUS.API_SUCCESS,
            data: response.data,
          };
        } else {
          setAlert(
            ALERT_TYPE.WARN,
            t("messages.warning.api.userFriendlyError"),
          );
          console.warn(
            t("messages.warning.api.outreachCohortStatusUpdateFailed", {
              cohortId,
            }),
          );
          return API_STATUS.API_ERROR;
        }
      } catch (e) {
        setAlert(ALERT_TYPE.ERROR, e.response?.data?.message || e.message);
        console.error(
          t("messages.error.api.outreachCohortStatusUpdateFailed"),
          e.response?.data?.message || e.message,
        );
        return API_STATUS.API_ERROR;
      }
    },
  };
  return api;
};

export default StudyRecruitmentApi;
